import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { InvoiceDialog } from "@/components/InvoiceDialog";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UtensilsCrossed, ClipboardList, ShoppingBag, Plus, Minus, Trash2, Loader2, ReceiptText, Wallet, Lock } from "lucide-react";
import { toast } from "sonner";

export default function MemberDashboard() {
  const { settings } = useSettings();
  const sym = settings.currency_symbol || "\u20b9";
  const [tab, setTab] = useState("menu");
  const [menu, setMenu] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [credit, setCredit] = useState(null);
  const [cart, setCart] = useState({});
  const [category, setCategory] = useState("All");
  const [payMode, setPayMode] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewInv, setViewInv] = useState(null);
  const [payCfg, setPayCfg] = useState({ modes: { cod: true, credit: true, pay_now: false }, razorpay_enabled: false, key_id: "" });

  const load = async () => {
    try {
      const [m, o, inv, c, pc] = await Promise.all([
        api.get("/menu"), api.get("/orders/my"), api.get("/invoices/my"), api.get("/credit/me"), api.get("/payments/config"),
      ]);
      setMenu(m.data); setOrders(o.data); setInvoices(inv.data); setCredit(c.data); setPayCfg(pc.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(() => {
    api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
  }, 12000); return () => clearInterval(t); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(menu.map((m) => m.category)))], [menu]);
  const filtered = category === "All" ? menu : menu.filter((m) => m.category === category);

  const cartItems = Object.values(cart);
  const cartCount = cartItems.reduce((s, c) => s + c.quantity, 0);
  const subtotal = cartItems.reduce((s, c) => s + c.price * c.quantity, 0);
  const [quote, setQuote] = useState(null);
  const cartSig = JSON.stringify(cartItems.map((c) => [c.id, c.quantity]));
  useEffect(() => {
    if (cartItems.length === 0) { setQuote(null); return; }
    const items = cartItems.map((c) => ({ menu_item_id: c.id, quantity: c.quantity }));
    const t = setTimeout(() => {
      api.post("/orders/quote", { items }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartSig]);
  const gst = quote ? quote.gst_amount : (settings.gst_enabled ? subtotal * (settings.gst_rate || 0) / 100 : 0);
  const total = quote ? quote.total : subtotal + gst;

  const addToCart = (item) => {
    const stock = Math.floor(item.stock ?? 0);
    const moq = Math.max(1, Math.floor(item.min_order_qty ?? 1));
    const cur = cart[item.id]?.quantity || 0;
    if (cur >= stock) { toast.error(`Only ${stock} available in stock`); return; }
    const target = cur === 0 ? moq : cur + 1;
    setCart((c) => ({ ...c, [item.id]: { ...item, quantity: Math.min(stock, target) } }));
    toast.success(`${item.name} added`);
  };
  const setQty = (id, delta) => {
    const item = cart[id];
    if (!item) return;
    const stock = Math.floor(item.stock ?? 0);
    const moq = Math.max(1, Math.floor(item.min_order_qty ?? 1));
    if (delta > 0 && item.quantity >= stock) { toast.error(`Only ${stock} available in stock`); return; }
    if (delta < 0 && item.quantity <= moq) { toast.error(`Minimum order quantity for ${item.name} is ${moq}`); return; }
    setCart((c) => {
      const q = (c[id]?.quantity || 0) + delta;
      if (q <= 0) { const n = { ...c }; delete n[id]; return n; }
      return { ...c, [id]: { ...c[id], quantity: q } };
    });
  };
  const setQtyDirect = (id, raw) => {
    const item = cart[id];
    if (!item) return;
    const stock = Math.floor(item.stock ?? 0);
    const moq = Math.max(1, Math.floor(item.min_order_qty ?? 1));
    const q = parseInt(raw, 10);
    if (isNaN(q) || q < moq) { toast.error(`Minimum order quantity for ${item.name} is ${moq}`); return; }
    if (q > stock) { toast.error(`Only ${stock} available in stock`); return; }
    setCart((c) => ({ ...c, [id]: { ...c[id], quantity: q } }));
  };

  const creditAvailable = credit ? credit.available : 0;
  const creditBlocked = payMode === "credit" && total > creditAvailable;

  useEffect(() => {
    const avail = { cod: !!payCfg.modes?.cod, credit: !!payCfg.modes?.credit && creditAvailable > 0, pay_now: !!payCfg.razorpay_enabled };
    if (!avail[payMode]) {
      const first = ["cod", "credit", "pay_now"].find((k) => avail[k]);
      if (first) setPayMode(first);
    }
  }, [payCfg, creditAvailable, payMode]);

  const placeOrder = async () => {
    if (payMode === "pay_now") return handlePayNow();
    setPlacing(true);
    try {
      const items = cartItems.map((c) => ({ menu_item_id: c.id, quantity: c.quantity }));
      await api.post("/orders", { items, payment_mode: payMode, notes: "" });
      toast.success("Order placed — kitchen notified");
      setCart({}); await load(); setTab("orders");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPlacing(false); }
  };

  const loadRzp = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const sc = document.createElement("script");
    sc.src = "https://checkout.razorpay.com/v1/checkout.js";
    sc.onload = () => resolve(true); sc.onerror = () => resolve(false);
    document.body.appendChild(sc);
  });

  const handlePayNow = async () => {
    setPlacing(true);
    try {
      const items = cartItems.map((c) => ({ menu_item_id: c.id, quantity: c.quantity }));
      const { data } = await api.post("/payments/razorpay/create-order", { items, payment_mode: "pay_now", notes: "" });
      const ok = await loadRzp();
      if (!ok) { toast.error("Could not load the payment gateway. Please check your connection."); setPlacing(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.razorpay_order_id, amount: data.amount, currency: data.currency,
        name: data.name, description: "Order payment", prefill: data.prefill, theme: { color: "#1f4d3a" },
        handler: async (res) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
            toast.success("Payment successful — order placed");
            setCart({}); await load(); setTab("orders");
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed"); }
          finally { setPlacing(false); }
        },
        modal: { ondismiss: () => setPlacing(false) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setPlacing(false); });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPlacing(false); }
  };

  const nav = [
    { key: "menu", label: "Dining Menu", icon: UtensilsCrossed },
    { key: "orders", label: "My Orders", icon: ClipboardList },
    { key: "bills", label: "My Bills", icon: ReceiptText },
    { key: "credit", label: "Credit Ledger", icon: Wallet },
  ];

  return (
    <Shell navItems={nav} active={tab} onNav={setTab} accent="member">
      <div className="max-w-6xl mx-auto px-5 lg:px-12 py-8 lg:py-12">
        {tab === "menu" && (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-2">À la carte</p>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-brand-text">The Club Table</h1>
                {!settings.club_open && (
                  <div className="mt-3 inline-flex items-center gap-2 text-brand-alert text-sm font-semibold" data-testid="club-closed-banner">
                    <Lock className="w-4 h-4" /> Club is currently closed{settings.closed_reason ? ` — ${settings.closed_reason}` : " — ordering is unavailable."}
                  </div>
                )}
              </div>
              <Sheet>
                <SheetTrigger asChild>
                  <Button data-testid="open-cart-button" className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-sm gap-2">
                    <ShoppingBag className="w-4 h-4" /> Cart
                    {cartCount > 0 && <span data-testid="cart-count" className="ml-1 bg-white text-brand-gold rounded-full w-5 h-5 text-xs flex items-center justify-center font-bold">{cartCount}</span>}
                  </Button>
                </SheetTrigger>
                <SheetContent className="bg-brand-bg w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
                  <SheetHeader><SheetTitle className="font-serif text-2xl tracking-tight text-left">Your Order</SheetTitle></SheetHeader>
                  <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4 space-y-3">
                    {cartItems.length === 0 && <p className="text-brand-muted text-sm">Your cart is empty.</p>}
                    {cartItems.map((c) => (
                      <div key={c.id} data-testid={`cart-item-${c.id}`} className="flex items-center gap-3 bg-white border border-brand-line rounded-sm p-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-brand-text truncate">{c.name}</p>
                          <p className="text-xs text-brand-muted">{money(c.price, sym)}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setQty(c.id, -1)} disabled={c.quantity <= Math.max(1, Math.floor(c.min_order_qty ?? 1))} className="w-7 h-7 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`cart-dec-${c.id}`}><Minus className="w-3 h-3" /></button>
                          <input type="number" min={Math.max(1, Math.floor(c.min_order_qty ?? 1))} max={Math.floor(c.stock ?? 0)}
                            value={c.quantity} data-testid={`cart-qty-${c.id}`}
                            onChange={(e) => setQtyDirect(c.id, e.target.value)}
                            className="w-12 text-center text-sm font-semibold border border-brand-line rounded-sm py-0.5" />
                          <button onClick={() => setQty(c.id, 1)} disabled={c.quantity >= Math.floor(c.stock ?? 0)} data-testid={`cart-inc-${c.id}`} className="w-7 h-7 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg disabled:opacity-40 disabled:cursor-not-allowed"><Plus className="w-3 h-3" /></button>
                          <button onClick={() => setCart((cur) => { const n = { ...cur }; delete n[c.id]; return n; })} data-testid={`cart-remove-${c.id}`} title="Remove item"
                            className="w-7 h-7 ml-1 border border-brand-alert/30 text-brand-alert rounded-sm flex items-center justify-center hover:bg-brand-alert/10"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-brand-line pt-4 space-y-3">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between text-brand-muted"><span>Subtotal</span><span>{money(subtotal, sym)}</span></div>
                      {quote?.item_discount_total > 0 && (
                        <div className="flex justify-between text-brand-ready" data-testid="cart-item-discount"><span>Item Discount</span><span>-{money(quote.item_discount_total, sym)}</span></div>
                      )}
                      {quote?.group_discount_total > 0 && (
                        <div className="flex justify-between text-brand-ready" data-testid="cart-group-discount"><span>Group Discount{quote.group_discount_percent ? ` (${quote.group_discount_percent}%)` : ""}</span><span>-{money(quote.group_discount_total, sym)}</span></div>
                      )}
                      {(quote?.charges || []).map((c, i) => (
                        <div key={i} className="flex justify-between text-brand-muted" data-testid={`cart-charge-${i}`}><span>{c.name}</span><span>{money(c.amount, sym)}</span></div>
                      ))}
                      {settings.gst_enabled && <div className="flex justify-between text-brand-muted"><span>GST</span><span>{money(gst, sym)}</span></div>}
                      <div className="flex justify-between font-semibold text-brand-text pt-1"><span>Total</span><span data-testid="cart-total">{money(total, sym)}</span></div>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-brand-muted mb-2">Payment Mode</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { k: "cod", l: "Cash on Delivery", disabled: !payCfg.modes?.cod },
                          { k: "credit", l: `Credit (${money(creditAvailable, sym)})`, disabled: !payCfg.modes?.credit || creditAvailable <= 0 },
                          { k: "pay_now", l: "Pay Now (Online)", disabled: !payCfg.razorpay_enabled },
                        ].map((p) => (
                          <button key={p.k} disabled={p.disabled}
                            onClick={() => { if (!p.disabled) setPayMode(p.k); }} data-testid={`pay-mode-${p.k}`}
                            className={`text-xs px-3 py-2 rounded-sm border transition-colors ${p.disabled ? "bg-brand-line/40 text-brand-muted border-brand-line cursor-not-allowed opacity-60" : payMode === p.k ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-line"}`}>{p.l}</button>
                        ))}
                      </div>
                      {(creditBlocked || creditAvailable <= 0) && <p className="text-xs text-brand-alert mt-2" data-testid="credit-limit-msg">Credit limit exhausted</p>}
                    </div>
                    <Button onClick={placeOrder} disabled={cartItems.length === 0 || placing || !settings.club_open || creditBlocked} data-testid="place-order-button"
                      className="w-full h-11 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm">
                      {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : !settings.club_open ? "Club Closed" : payMode === "pay_now" ? "Pay Now" : "Place Order"}
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <div className="flex gap-2 flex-wrap mb-8">
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)} data-testid={`category-${c}`}
                  className={`px-4 py-2 text-sm rounded-sm border transition-colors ${category === c ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-line text-brand-muted hover:border-brand-primary"}`}>{c}</button>
              ))}
            </div>

            {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((item, i) => {
                  const moq = Math.max(1, Math.floor(item.min_order_qty ?? 1));
                  const belowMoq = Math.floor(item.stock ?? 0) < moq;
                  const out = !item.available || belowMoq;
                  const hasImage = item.image_url && item.image_url.trim() !== "";
                  const disc = Number(item.discount_percent) || 0;
                  const hasDisc = disc > 0;
                  const dprice = Math.round(item.price * (1 - disc / 100) * 100) / 100;
                  return (
                    <div key={item.id} data-testid={`menu-item-${item.id}`} style={{ animationDelay: `${i * 50}ms` }}
                      className="group bg-white border border-brand-line rounded-md overflow-hidden animate-fade-up transition-transform duration-300 hover:-translate-y-1 flex flex-col relative">
                      {hasImage && (
                        <div className="aspect-[4/3] overflow-hidden bg-brand-bg relative">
                          {hasDisc && (
                            <span data-testid={`discount-badge-${item.id}`}
                              className="absolute top-3 left-3 z-10 bg-brand-gold text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm shadow-sm">
                              {disc}% OFF
                            </span>
                          )}
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          {out && <div className="absolute inset-0 bg-black/50 flex items-center justify-center px-4 text-center"><span className="text-white text-sm font-semibold uppercase tracking-wider">{belowMoq && item.available ? `Below Min. Order Qty (${moq})` : "Out of Stock"}</span></div>}
                        </div>
                      )}
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex flex-col gap-1.5 min-w-0">
                            <h3 className="font-serif text-xl tracking-tight text-brand-text leading-tight">{item.name}</h3>
                            {hasDisc && !hasImage && (
                              <span data-testid={`discount-badge-${item.id}`}
                                className="inline-block w-fit bg-brand-gold text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">
                                {disc}% OFF
                              </span>
                            )}
                          </div>
                          {hasDisc ? (
                            <span className="text-right whitespace-nowrap leading-tight">
                              <span className="block text-xs text-brand-muted line-through">{money(item.price, sym)}</span>
                              <span className="block font-semibold text-brand-primary" data-testid={`discounted-price-${item.id}`}>{money(dprice, sym)}</span>
                            </span>
                          ) : (
                            <span className="font-semibold text-brand-primary whitespace-nowrap">{money(item.price, sym)}</span>
                          )}
                        </div>
                        {out && !hasImage && <span className="inline-block w-fit mt-2 text-[10px] uppercase tracking-wider font-semibold text-brand-alert border border-brand-alert/40 rounded-sm px-2 py-0.5" data-testid={`out-of-stock-${item.id}`}>{belowMoq && item.available ? `Stock below minimum order qty of ${moq}` : "Out of Stock"}</span>}
                        {!out && <p className="text-xs font-semibold text-brand-primary mt-1" data-testid={`stock-${item.id}`}>In Stock: {Math.floor(item.stock ?? 0)}{moq > 1 && <span className="text-brand-muted font-normal"> · Min. {moq}</span>}</p>}
                        {item.description && <p className="text-sm text-brand-muted mt-2 line-clamp-2">{item.description}</p>}
                        <Button onClick={() => addToCart(item)} disabled={out || (cart[item.id]?.quantity || 0) >= Math.floor(item.stock ?? 0)} data-testid={`add-to-cart-${item.id}`}
                          className="w-full mt-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm gap-2 disabled:opacity-50">
                          <Plus className="w-4 h-4" /> {belowMoq && item.available ? `Below Min. Qty (${moq})` : out ? "Unavailable" : (cart[item.id]?.quantity || 0) >= Math.floor(item.stock ?? 0) ? "Max in cart" : "Add to Order"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === "orders" && (
          <>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-2">History</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-brand-text mb-8">My Orders</h1>
            {orders.length === 0 ? <p className="text-brand-muted">No orders yet.</p> : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`order-${o.id}`} className="bg-white border border-brand-line rounded-md p-5">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <p className="font-serif text-xl tracking-tight">{o.order_number}</p>
                        <p className="text-xs text-brand-muted mt-1">{fmtDate(o.created_at)} · {o.payment_mode.toUpperCase()}</p>
                      </div>
                      <StatusBadge status={o.status} />
                    </div>
                    <div className="mt-4 pt-4 border-t border-brand-line space-y-1">
                      {o.items.map((it, idx) => (
                        <div key={idx} className="flex justify-between text-sm"><span className="text-brand-text">{it.quantity} × {it.name}</span><span className="text-brand-muted">{money(it.price * it.quantity, sym)}</span></div>
                      ))}
                      <div className="flex justify-between pt-2 font-semibold"><span>Total</span><span>{money(o.total, sym)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "bills" && (
          <>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-2">Invoices</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-brand-text mb-8">My Bills</h1>
            {invoices.length === 0 ? <p className="text-brand-muted">No invoices yet.</p> : (
              <div className="space-y-3">
                {invoices.map((inv) => (
                  <div key={inv.id} data-testid={`invoice-${inv.id}`} className="bg-white border border-brand-line rounded-md p-5 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-semibold text-brand-text">{inv.invoice_number}</p>
                      <p className="text-xs text-brand-muted">{fmtDate(inv.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right"><p className="font-serif text-xl">{money(inv.total, sym)}</p><StatusBadge status={inv.cancelled ? "cancelled" : inv.status} /></div>
                      <Button onClick={() => setViewInv(inv)} data-testid={`view-invoice-${inv.id}`} variant="outline" className="rounded-sm border-brand-line gap-2"><ReceiptText className="w-4 h-4" /> View</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "credit" && credit && (
          <>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-2">Account</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-brand-text mb-8">Credit Ledger</h1>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[{ l: "Credit Limit", v: credit.credit_limit }, { l: "Outstanding", v: credit.outstanding }, { l: "Available", v: credit.available }].map((c, i) => (
                <div key={i} className="bg-white border border-brand-line rounded-md p-6" data-testid={`credit-${c.l.toLowerCase().replace(" ", "-")}`}>
                  <p className="text-xs uppercase tracking-wider text-brand-muted">{c.l}</p>
                  <p className="font-serif text-3xl tracking-tight text-brand-text mt-2">{money(c.v, sym)}</p>
                </div>
              ))}
            </div>
            <h3 className="text-lg font-semibold mb-3">Credit Invoices</h3>
            {credit.invoices.length === 0 ? <p className="text-brand-muted">No credit invoices.</p> : (
              <div className="space-y-2">
                {credit.invoices.map((inv) => (
                  <div key={inv.id} className="bg-white border border-brand-line rounded-sm p-4 flex justify-between items-center">
                    <div><p className="font-medium">{inv.invoice_number}</p><p className="text-xs text-brand-muted">{fmtDate(inv.created_at)}</p></div>
                    <div className="text-right"><p className="font-semibold">{money(inv.total, sym)}</p><p className="text-xs text-brand-muted">Due {money(inv.balance_due, sym)}</p></div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <InvoiceDialog invoice={viewInv} open={!!viewInv} onClose={() => setViewInv(null)} />
    </Shell>
  );
}
